/**
 * BrandMark — renders the current tenant's branding as either:
 * - the uploaded logo image (preferred), OR
 * - the tenant's text name beside a neutral Building2 icon placeholder, OR
 * - the "Elara" wordmark fallback when the tenant hasn't customised anything.
 *
 * Multi-tenant safe: pulls from BrandingContext which fetches via a
 * tenant-scoped backend endpoint.
 */
import React from 'react';
import { Building2 } from 'lucide-react';
import { useBranding } from '../context/BrandingContext';

export function BrandMark({ variant = 'sidebar', className = '' }) {
  const { orgName, logoUrl, hasCustomBrand } = useBranding();

  // Platform fallback — no tenant customisation.
  if (!hasCustomBrand) {
    return (
      <span
        className={`font-bold text-[#1ABC9C] ${variant === 'sidebar' ? 'text-2xl' : 'text-xl'} ${className}`}
        data-testid="brand-mark-elara"
      >
        Elara
      </span>
    );
  }

  // Custom logo uploaded — show the image. The `img` sends the auth
  // cookie automatically, so tenant isolation holds.
  if (logoUrl) {
    return (
      <div className={`flex items-center gap-2.5 ${className}`} data-testid="brand-mark-logo">
        <img
          src={logoUrl}
          alt={`${orgName} logo`}
          className={variant === 'sidebar' ? 'h-8 w-auto max-w-[140px] object-contain' : 'h-6 w-auto max-w-[120px] object-contain'}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <span className="sr-only">{orgName}</span>
      </div>
    );
  }

  // Name set, no logo — text + neutral placeholder.
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="brand-mark-text">
      <span className={`inline-flex items-center justify-center rounded-md bg-[#1ABC9C]/15 text-[#1ABC9C] ${variant === 'sidebar' ? 'w-8 h-8' : 'w-6 h-6'}`}>
        <Building2 className={variant === 'sidebar' ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
      </span>
      <span className={`font-semibold text-white truncate ${variant === 'sidebar' ? 'text-xl max-w-[160px]' : 'text-base max-w-[140px]'}`}>
        {orgName}
      </span>
    </div>
  );
}

export default BrandMark;
